import * as yup from 'yup';
import { Formik } from 'formik';
import { keys, length } from 'ramda';
import { useState, memo } from 'react';
import { Box, InputAdornment, Typography } from '@mui/material';
import { AlertBoxMsg, ChipInput, ChipInputTagErrors } from '@qrios/frontend-library';

import {
  emailValidation,
  msisdnValidationSimple,
  balanceThresholdValidation
} from '../../../../../../../../../constants/validation';
import { naira } from '../../../../../../../../../constants/utils';
import WithFormLayout from '../../../../../../../../common/with-form-layout';
import { diffObject } from '../../../../../../../../../constants/helpers/diff';
import { FORM_INPUT_ERROR_MSGS } from '../../../../../../../../../constants/errors';
import SwitchableInput from '../../../../../../../../common/fields/switchable-input';
import AmountMaskedInput from '../../../../../../../../common/fields/amount-masked-input';
import { convertAmountToApiFormat } from '../../../../../../../../common/fields/amount-masked-input/utils';

import { NOTIFICATION_SETTINGS, LowBalanceNotificationResponseData } from '../interfaces';

const validationSchema = yup.object().shape({
  [NOTIFICATION_SETTINGS.THRESHOLD]: balanceThresholdValidation,
  [NOTIFICATION_SETTINGS.EMAIL]: yup
    .array(emailValidation)
    .test('emailLengthValidation', FORM_INPUT_ERROR_MSGS.MAX_VALUES_LIMIT(5), function (this) {
      return this.parent[NOTIFICATION_SETTINGS.EMAIL]?.length <= 5;
    }),
  [NOTIFICATION_SETTINGS.USSD_PUSH]: yup
    .array(msisdnValidationSimple)
    .test('ussdPushLengthValidation', FORM_INPUT_ERROR_MSGS.MAX_VALUES_LIMIT(5), function (this) {
      return this.parent[NOTIFICATION_SETTINGS.USSD_PUSH]?.length <= 5;
    })
});

type BusinessError = any;

interface FormikInitialValues {
  [NOTIFICATION_SETTINGS.THRESHOLD]: string;
  [NOTIFICATION_SETTINGS.EMAIL]: string[];
  [NOTIFICATION_SETTINGS.USSD_PUSH]: string[];
}

interface Props {
  initialValues: LowBalanceNotificationResponseData;
  onSubmit: (props: any) => void;
  handleClose: () => void;
  loading: boolean;
  error?: BusinessError | null;
}

const EditNotificationSettings = ({ initialValues, onSubmit, handleClose, error, loading }: Props) => {
  const { threshold, email, ussdPush } = initialValues;
  const [emailSwitch, setEmailSwitch] = useState<boolean>(email.enabled);
  const [ussdPushSwitch, setUssdPushSwitch] = useState<boolean>(ussdPush.enabled);

  const formikInitialValues: FormikInitialValues = {
    [NOTIFICATION_SETTINGS.THRESHOLD]: threshold.toString(),
    [NOTIFICATION_SETTINGS.EMAIL]: initialValues.email.recipients,
    [NOTIFICATION_SETTINGS.USSD_PUSH]: initialValues.ussdPush.recipients.map(
      (ussdPushPhoneNumber: string) => ussdPushPhoneNumber
    )
  };

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={values => {
        const updatedNotidicationSettings: LowBalanceNotificationResponseData = {
          threshold: convertAmountToApiFormat(values[NOTIFICATION_SETTINGS.THRESHOLD]),
          email: {
            enabled: emailSwitch,
            recipients: values.email
          },
          ussdPush: {
            enabled: ussdPushSwitch,
            recipients: values.ussdPush
          }
        };

        onSubmit(updatedNotidicationSettings);
      }}
      validationSchema={validationSchema}
      validateOnBlur
    >
      {({ setFieldValue, values, errors, touched, handleBlur, handleSubmit }) => {
        const handleChange = (field: string, value: unknown) => {
          setFieldValue(field, value);
        };

        const valuesUnchanged =
          length(
            keys(
              diffObject(initialValues, {
                threshold: Number(values.threshold),
                email: {
                  enabled: emailSwitch,
                  recipients: values.email
                },
                ussdPush: {
                  enabled: ussdPushSwitch,
                  recipients: values.ussdPush
                }
              })
            )
          ) === 0;

        return (
          <WithFormLayout
            formInfoText="Edit your low balance notification settings"
            submitLabel="Update settings"
            secondaryLabel={valuesUnchanged ? 'Close' : 'Close without update'}
            onSubmit={handleSubmit}
            onSecondary={handleClose}
            loading={loading}
            error={error}
            disableSubmit={valuesUnchanged}
          >
            <Box mb={2}>
              <AlertBoxMsg severity="info">
                <Box display="flex" flexDirection="column">
                  Please enable your notification methods if you want to receive information when the balance will be
                  equal or drop under the threshold.
                  <ul>
                    <li>Email address(es) (up to 5 emails)</li>
                    <li>USSD push phone number(s) (up to 5 numbers)</li>
                  </ul>
                </Box>
              </AlertBoxMsg>
            </Box>
            <Box mb={4}>
              <AmountMaskedInput
                handleChange={e => {
                  handleChange(e.target.name, e.target.value);
                }}
                handleBlur={handleBlur}
                name={NOTIFICATION_SETTINGS.THRESHOLD}
                label="Balance threshold"
                placeholder="Provide balance threshold"
                value={values[NOTIFICATION_SETTINGS.THRESHOLD]}
                error={errors[NOTIFICATION_SETTINGS.THRESHOLD] as string}
                touched={touched[NOTIFICATION_SETTINGS.THRESHOLD] as boolean}
                startAdornment={<InputAdornment position="start">{naira}</InputAdornment>}
              />
            </Box>
            <Box mb={2}>
              <Typography variant="h3">Notification methods</Typography>
            </Box>
            <SwitchableInput
              switchState={emailSwitch}
              handleSwitchState={() => {
                setEmailSwitch(!emailSwitch);
              }}
            >
              <ChipInput
                handleChange={handleChange}
                name={NOTIFICATION_SETTINGS.EMAIL}
                label="Email address(es)"
                placeholder="Provide email address(es)"
                handleBlur={handleBlur}
                value={values[NOTIFICATION_SETTINGS.EMAIL]}
                error={errors[NOTIFICATION_SETTINGS.EMAIL] as string}
                tagErrors={errors[NOTIFICATION_SETTINGS.EMAIL] as ChipInputTagErrors}
                disabled={!emailSwitch}
              />
            </SwitchableInput>
            <SwitchableInput
              switchState={ussdPushSwitch}
              handleSwitchState={() => {
                setUssdPushSwitch(!ussdPushSwitch);
              }}
            >
              <ChipInput
                handleChange={handleChange}
                name={NOTIFICATION_SETTINGS.USSD_PUSH}
                label="USSD push phone number(s)"
                placeholder="Provide phone number(s)"
                handleBlur={handleBlur}
                value={values[NOTIFICATION_SETTINGS.USSD_PUSH]}
                error={errors[NOTIFICATION_SETTINGS.USSD_PUSH] as string}
                tagErrors={errors[NOTIFICATION_SETTINGS.USSD_PUSH] as ChipInputTagErrors}
                disabled={!ussdPushSwitch}
              />
            </SwitchableInput>
          </WithFormLayout>
        );
      }}
    </Formik>
  );
};

export default memo(EditNotificationSettings);
